import React, { useState, useRef, useEffect } from 'react';
import { Card, Tabs, Grid, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { 
  HomeOutlined, 
  CarOutlined, 
  AppstoreOutlined, 
  BankOutlined,
  UserOutlined,
  PercentageOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getAllTotals, hasWorkspaceStats } from '@/utils/workspaceStats';
import { useCRUDTour } from '@/hooks/useCRUDTour';
import { TourButton } from '@/components/common/TourButton';
import { Blocks } from './Blocks';
import { ApartmentUnits } from './ApartmentUnits';
import { Parkings } from './Parkings';
import { Amenities } from './Amenities';

export const Properties: React.FC = () => {
  const { t } = useTranslation(['properties', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { xs } = Grid.useBreakpoint();
  const { workspaceStats } = useAuth();
  const { startCRUDTour, cleanup } = useCRUDTour();

  // Get active tab from URL path
  const activeTab = location.pathname.split('/').pop() || 'blocks';

  // Set default URL to /blocks on initial load
  useEffect(() => {
    if (location.pathname === '/resources/properties') {
      // Preserve search parameters (like ?tour=true) when redirecting
      const searchParams = location.search;
      navigate(`/resources/properties/blocks${searchParams}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // Clean up tours when navigating between tabs
  useEffect(() => {
    // Clean up immediately when tab changes
    cleanup();
    
    // Force a longer delay to ensure cleanup is complete and DOM is reset
    const timeoutId = setTimeout(() => {
      // Force a DOM cleanup to ensure all Driver.js elements are removed
      const driverElements = document.querySelectorAll('[data-driver-highlighted-element]');
      driverElements.forEach(el => el.removeAttribute('data-driver-highlighted-element'));
      
      const overlays = document.querySelectorAll('.driver-overlay, .driver-popover, .driver-popover-tip');
      overlays.forEach(el => el.remove());
      
      // Additional cleanup: remove any remaining Driver.js classes and attributes
      const driverClasses = document.querySelectorAll('.driver-highlighted-element, .driver-popover, .driver-overlay');
      driverClasses.forEach(el => el.remove());
      
      // Reset any elements that might have been modified by Driver.js
      const highlightedElements = document.querySelectorAll('[data-driver-highlighted-element]');
      highlightedElements.forEach(el => {
        el.removeAttribute('data-driver-highlighted-element');
        if (el instanceof HTMLElement) {
          el.style.position = '';
          el.style.zIndex = '';
        }
      });
    }, 300);
    
    return () => {
      clearTimeout(timeoutId);
      cleanup();
    };
  }, [activeTab, cleanup]);

  // Get comprehensive stats from workspace stats or fallback to mock values
  const getStats = () => {
    // Default fallback values - only used when workspace stats are completely unavailable
    const fallbackStats = {
      totalBlocks: 0,
      totalApartmentUnits: 0,
      totalParkings: 0,
      totalAmenities: 0,
      totalResidents: 0,
      occupancyRate: 0
    };

    if (hasWorkspaceStats(workspaceStats) && workspaceStats.blocks && Object.keys(workspaceStats.blocks).length > 0) {
      const totals = getAllTotals(workspaceStats);
      
      // Calculate occupancy rate (assuming occupied units vs total units)
      const occupancyRate = totals.totalApartmentUnits > 0 
        ? Math.round((totals.totalResidents / totals.totalApartmentUnits) * 100)
        : 0;
      
      return [
        {
          title: t('properties:blocks.title'),
          value: totals.totalBlocks || 0,
          icon: <HomeOutlined style={{ color: '#1890ff' }} />
        },
        {
          title: t('properties:apartmentUnits.title'),
          value: totals.totalApartmentUnits || 0,
          icon: <AppstoreOutlined style={{ color: '#52c41a' }} />
        },
        {
          title: t('properties:parkings.title'),
          value: totals.totalParkings || 0,
          icon: <CarOutlined style={{ color: '#faad14' }} />
        },
        {
          title: t('properties:amenity.title'),
          value: totals.totalAmenities || 0,
          icon: <BankOutlined style={{ color: '#722ed1' }} />
        },
        {
          title: 'Residents',
          value: totals.totalResidents || 0,
          icon: <UserOutlined style={{ color: '#13c2c2' }} />
        },
        {
          title: 'Occupancy',
          value: `${occupancyRate}%`,
          icon: <PercentageOutlined style={{ color: '#eb2f96' }} />
        }
      ];
    }

    // Return fallback stats when workspace stats are not available
    return [
      {
        title: t('properties:blocks.title'),
        value: fallbackStats.totalBlocks,
        icon: <HomeOutlined style={{ color: '#1890ff' }} />
      },
      {
        title: t('properties:apartmentUnits.title'),
        value: fallbackStats.totalApartmentUnits,
        icon: <AppstoreOutlined style={{ color: '#52c41a' }} />
      },
      {
        title: t('properties:parkings.title'),
        value: fallbackStats.totalParkings,
        icon: <CarOutlined style={{ color: '#faad14' }} />
      },
      {
        title: t('properties:amenity.title'),
        value: fallbackStats.totalAmenities,
        icon: <BankOutlined style={{ color: '#722ed1' }} />
      },
      {
        title: 'Residents',
        value: fallbackStats.totalResidents,
        icon: <UserOutlined style={{ color: '#13c2c2' }} />
      },
      {
        title: 'Occupancy',
        value: `${fallbackStats.occupancyRate}%`,
        icon: <PercentageOutlined style={{ color: '#eb2f96' }} />
      }
    ];
  };

  const stats = getStats();

  // Tour functionality
  const handleStartTour = () => {
    // Get the record count for the currently active tab
    const getCurrentTabRecordCount = () => {
      switch (activeTab) {
        case 'blocks':
          return stats.find(stat => stat.title === t('properties:blocks.title'))?.value || 0;
        case 'units':
          return stats.find(stat => stat.title === t('properties:apartmentUnits.title'))?.value || 0;
        case 'parkings':
          return stats.find(stat => stat.title === t('properties:parkings.title'))?.value || 0;
        case 'amenities':
          return stats.find(stat => stat.title === t('properties:amenity.title'))?.value || 0;
        default:
          return 0;
      }
    };

    const currentTabRecordCount = getCurrentTabRecordCount();
    const hasRecords = typeof currentTabRecordCount === 'number' ? currentTabRecordCount > 0 : false;
    
    // Get the record type name for the current tab
    const getCurrentTabRecordType = () => {
      switch (activeTab) {
        case 'blocks':
          return 'block';
        case 'units':
          return 'apartment unit';
        case 'parkings':
          return 'parking';
        case 'amenities':
          return 'amenity';
        default:
          return 'property';
      }
    };
    
    startCRUDTour({
      pageName: 'Properties Management',
      recordType: getCurrentTabRecordType(),
      hasRecords,
      recordCount: typeof currentTabRecordCount === 'number' ? currentTabRecordCount : 0
    });
  };

  const tabItems = [
    {
      key: 'blocks',
      label: t('properties:blocks.title'),
      children: <Blocks />,
    },
    {
      key: 'units',
      label: t('properties:apartmentUnits.title'),
      children: <ApartmentUnits />,
    },
    {
      key: 'parkings',
      label: t('properties:parkings.title'),
      children: <Parkings />,
    },
    {
      key: 'amenities',
      label: t('properties:amenity.title'),
      children: <Amenities />,
    },
  ];

  const scrollToSlide = (index: number) => {
    if (sliderRef.current) {
      const cardWidth = 140; // Reduced width
      sliderRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setCurrentSlide(index);
    }
  };

  const handleScroll = () => {
    if (sliderRef.current) {
      const scrollLeft = sliderRef.current.scrollLeft;
      const cardWidth = 140; // Reduced width
      const newSlide = Math.round(scrollLeft / cardWidth);
      if (newSlide !== currentSlide) {
        setCurrentSlide(newSlide);
      }
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, [currentSlide]);

  const handleTabChange = (key: string) => {
    navigate(`/resources/properties/${key}`);
  };

  return (
    <div>
      {/* Enhanced Stats Section */}
      <div className="stats-section" style={{ marginBottom: 24 }}>
        <div
          ref={sliderRef}
          style={{
            display: 'flex',
            overflowX: xs ? 'auto' : 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            gap: xs ? '12px' : '8px',
            padding: '8px 0',
            flexWrap: xs ? 'nowrap' : 'wrap',
            justifyContent: xs ? 'flex-start' : 'center'
          }}
        >
          {stats.map((stat, idx) => (
            <Card
              key={idx}
              style={{
                minWidth: 120,
                maxWidth: 150,
                width: '100%',
                flex: xs ? '0 0 auto' : '1 1 0',
                cursor: 'pointer',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                border: '1px solid #f0f0f0',
                margin: xs ? 0 : '0 4px'
              }}
              onClick={() => scrollToSlide(idx)}
              hoverable
              styles={{
                body: {
                  padding: '16px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '4px'
              }}>
                <div style={{ fontSize: '20px' }}>
                  {stat.icon}
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700',
                  color: '#262626'
                }}>
                  {stat.value}
                </div>
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#262626',
                textAlign: 'center',
                lineHeight: '1.2',
                fontWeight: '600'
              }}>
                {stat.title}
              </div>
            </Card>
          ))}
        </div>
        
        {/* Progress Indicators for Mobile */}
        {xs && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '6px', 
            marginTop: '12px' 
          }}>
            {stats.map((_, idx) => (
              <div
                key={idx}
                onClick={() => scrollToSlide(idx)}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: currentSlide === idx ? '#1890ff' : '#d9d9d9',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s'
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Tour Button */}
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <Space>
          <TourButton onClick={handleStartTour} />
        </Space>
      </div>

      <Tabs
        className="properties-tabs"
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />
    </div>
  );
}; 